import React from 'react';
import { FormField as IFormField } from '../types/form';
import { useFormStore } from '../store/formStore';
import { Info, MapPin, Search, Check, AlertCircle, Eye, EyeOff, SkipForward, User, Mail, Home, Calendar, Building2, Briefcase, Users, FileText, DollarSign, Clock, Sparkles, Upload } from 'lucide-react';
import clsx from 'clsx';
import { isFieldConditionallyRequired, shouldSkipField } from '../utils/conditionalValidation';
import { ExampleAnswers } from './ExampleAnswers';
import { getSmartDefault, getSmartDefaultHint } from '../utils/smartDefaults';
import { FileUploadField } from './FileUploadField';

interface FormFieldProps {
  field: IFormField;
  onSkip?: () => void;
}

import { AddressResult, hybridAddressSearch, validateUKPostcode, formatPostcode } from '../services/addressLookup';

const getFieldIcon = (fieldId: string, question: string) => {
  const lowerQuestion = question.toLowerCase();
  const lowerId = fieldId.toLowerCase();

  if (lowerId.includes('name')) return User;
  if (lowerId.includes('email')) return Mail;
  if (lowerId.includes('address') || lowerId.includes('property')) return Home;
  if (lowerId.includes('date') || lowerId.includes('tenancy')) return Calendar;
  if (lowerId.includes('rent') || lowerId.includes('deposit') || lowerId.includes('fee')) return DollarSign;
  if (lowerId.includes('company') || lowerId.includes('business')) return Building2;
  if (lowerId.includes('tenant') && !lowerId.includes('name')) return Users;
  if (lowerId.includes('landlord') && !lowerId.includes('name')) return Briefcase;
  if (lowerId.includes('duration') || lowerId.includes('period')) return Clock;
  if (lowerId.includes('upload') || lowerId.includes('document') || lowerId.includes('existingdocument')) return Upload;

  return FileText;
};

const getFieldImage = (fieldId: string, question: string) => {
  const lowerId = fieldId.toLowerCase();

  if (lowerId.includes('jurisdiction') || lowerId.includes('location') || lowerId.includes('property')) {
    return 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=1200&fit=crop';
  }
  if (lowerId.includes('tenant') && !lowerId.includes('email') && !lowerId.includes('address')) {
    return 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=1200&fit=crop';
  }
  if (lowerId.includes('landlord') && !lowerId.includes('email') && !lowerId.includes('address')) {
    return 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=1200&fit=crop';
  }
  if (lowerId.includes('rent') || lowerId.includes('deposit')) {
    return 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=1200&fit=crop';
  }
  if (lowerId.includes('address')) {
    return 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=1200&fit=crop';
  }
  if (lowerId.includes('date') || lowerId.includes('duration')) {
    return 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=800&h=1200&fit=crop';
  }

  return 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=1200&fit=crop';
};

const getContextualInfo = (fieldId: string, question: string) => {
  const lowerId = fieldId.toLowerCase();

  if (lowerId.includes('jurisdiction')) {
    return {
      title: 'Legal Jurisdiction',
      description: 'The location of your property determines which laws apply to your tenancy agreement.',
      points: [
        'England & Wales follow the Housing Act 1988',
        'Scotland has separate tenancy legislation',
        'Different regions have different legal requirements'
      ]
    };
  }

  if (lowerId.includes('tenanttype')) {
    return {
      title: 'Tenant Classification',
      description: 'The type of tenant affects the legal framework and protections that apply.',
      points: [
        'Individual tenants have residential protections',
        'Company lets are commercial arrangements',
        'Different notice periods apply to each type'
      ]
    };
  }

  if (lowerId.includes('landlordresidence')) {
    return {
      title: 'Resident Landlord',
      description: 'A live-in landlord creates a different legal arrangement known as a lodger agreement.',
      points: [
        'Creates an excluded tenancy (lodger)',
        'Fewer legal protections for occupiers',
        'Different deposit protection rules apply'
      ]
    };
  }

  if (lowerId.includes('mainresidence')) {
    return {
      title: 'Primary Residence',
      description: 'An Assured Shorthold Tenancy provides the strongest protections for residential tenants.',
      points: [
        'AST provides strong tenant protections',
        'Deposit must be legally protected',
        'Minimum 2-month notice for Section 21'
      ]
    };
  }

  if (lowerId.includes('rent')) {
    return {
      title: 'Rent Amount',
      description: 'The monthly rent must be clearly stated and follow legal requirements.',
      points: [
        'Must be clearly stated in the agreement',
        'Payment frequency should be specified',
        'Rent increases need proper notice'
      ]
    };
  }

  if (lowerId.includes('deposit')) {
    return {
      title: 'Security Deposit',
      description: 'The deposit must be protected by law within 30 days of receipt.',
      points: [
        'Must be protected within 30 days',
        'Maximum 5 weeks rent (under £50k/year)',
        'Protected by government-approved schemes'
      ]
    };
  }

  if (lowerId.includes('email')) {
    return {
      title: 'Email Address',
      description: 'A valid email is required for legal document delivery and e-signatures.',
      points: [
        'Used for legal document delivery',
        'Required for e-signature process',
        'Important for communication records'
      ]
    };
  }

  if (lowerId.includes('address')) {
    return {
      title: 'Property Address',
      description: 'The complete legal address is essential for all official documentation.',
      points: [
        'Must be the complete legal address',
        'Include postcode for verification',
        'Required for deposit protection'
      ]
    };
  }

  if (lowerId.includes('existingdocument') || lowerId.includes('upload')) {
    return {
      title: 'Document Upload',
      description: 'Upload your existing tenancy document so our AI can extract the relevant information.',
      points: [
        'Supports PDF, Word, and text files',
        'Text is extracted and analysed by AI',
        'You can also paste text directly'
      ]
    };
  }

  return {
    title: 'Important Information',
    description: 'Please ensure all information provided is accurate and up to date.',
    points: [
      'All details are legally binding',
      'Review carefully before proceeding',
      'Contact support if unsure'
    ]
  };
};

export const FormField: React.FC<FormFieldProps> = ({ field, onSkip }) => {
  const { responses, setResponse } = useFormStore();
  const value = responses[field.id];

  const isRequired = field.validation?.conditionalRequired
    ? isFieldConditionallyRequired(field, responses)
    : field.validation?.required ?? false;

  const shouldBeSkipped = shouldSkipField(field, responses);

  if (shouldBeSkipped) {
    return null;
  }

  const [addressQuery, setAddressQuery] = React.useState('');
  const [addressResults, setAddressResults] = React.useState<AddressResult[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [showManualEntry, setShowManualEntry] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string>('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [addressError, setAddressError] = React.useState<string>('');
  const searchTimeoutRef = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    if (isRequired && !value) {
      setValidationError('');
    } else if (field.validation?.pattern && value) {
      const regex = new RegExp(field.validation.pattern);
      if (!regex.test(value as string)) {
        if (field.type === 'text' && field.question.toLowerCase().includes('email')) {
          setValidationError('Please enter a valid email address');
        } else {
          setValidationError('Please enter a valid format');
        }
      } else {
        setValidationError('');
      }
    } else {
      setValidationError('');
    }
  }, [value, field.validation, isRequired]);

  React.useEffect(() => {
    const isAddressField = field.question.toLowerCase().includes('address') && !field.question.toLowerCase().includes('email');

    if (isAddressField) {
      setAddressQuery((value as string) || '');
      setAddressResults([]);
      setAddressError('');
      setShowManualEntry(false);
    }
  }, [field.id, value]);

  const searchAddresses = React.useCallback(async (query: string) => {
    if (query.length < 3) {
      setAddressResults([]);
      setAddressError('');
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setIsSearching(true);
    setAddressError('');

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const googleApiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
        const results = await hybridAddressSearch(query, googleApiKey);

        if (results.length === 0) {
          setAddressError('No addresses found. Try entering a postcode or use manual entry.');
        }

        setAddressResults(results);
      } catch (error) {
        console.error('Address search error:', error);
        setAddressError(
          error instanceof Error
            ? error.message
            : 'Unable to search addresses. Please use manual entry.'
        );
        setAddressResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500);
  }, []);

  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleAddressSelect = (address: AddressResult) => {
    setResponse(field.id, address.formatted_address);
    setAddressQuery(address.formatted_address);
    setAddressResults([]);
    setAddressError('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setResponse(field.id, e.target.value);
  };

  const handleCheckboxChange = (option: string) => {
    const currentValues = (responses[field.id] as string[]) || [];
    const newValues = currentValues.includes(option)
      ? currentValues.filter(v => v !== option)
      : [...currentValues, option];
    setResponse(field.id, newValues);
  };

  const isEmailField = field.question.toLowerCase().includes('email');
  const isAddressField = field.question.toLowerCase().includes('address') && !isEmailField;
  const isPasswordField = field.type === 'text' && field.question.toLowerCase().includes('password');

  const FieldIcon = getFieldIcon(field.id, field.question);
  const fieldImage = getFieldImage(field.id, field.question);
  const contextInfo = getContextualInfo(field.id, field.question);

  return (
    <div className="flex flex-col lg:flex-row min-h-[600px] rounded-2xl border border-gray-200 shadow-sm">
      {/* Left Side - Info Panel with Background Image */}
      <div className="lg:w-2/5 relative bg-gradient-to-br from-gray-900 to-gray-800 text-white overflow-hidden flex-shrink-0 rounded-t-2xl lg:rounded-t-none lg:rounded-l-2xl">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={fieldImage}
            alt={field.question}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-gray-900/90"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 p-8 lg:p-12 flex flex-col justify-center min-h-[400px] lg:min-h-[600px]">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl mb-6">
              <FieldIcon className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-2xl font-semibold mb-3">{contextInfo.title}</h3>
            <p className="text-gray-300 leading-relaxed text-sm">
              {contextInfo.description}
            </p>
          </div>

          <div className="space-y-4">
            {contextInfo.points.map((point, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                    <Check className="w-3 h-3 text-gray-400" />
                  </div>
                </div>
                <p className="text-sm text-gray-200 leading-relaxed">{point}</p>
              </div>
            ))}
          </div>

          {/* Help Text */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-2">Need Assistance?</p>
            <p className="text-sm text-gray-300">Our support team is available to help you complete this form.</p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="lg:w-3/5 bg-white p-8 lg:p-12 flex flex-col justify-center overflow-visible rounded-b-2xl lg:rounded-b-none lg:rounded-r-2xl">
        <div className="max-w-xl mx-auto w-full space-y-8">
          {/* Question Header */}
          <div>
            <label className="block text-3xl font-semibold text-gray-900 leading-tight mb-3">
              {field.question}
              {isRequired && <span className="text-red-500 ml-2">*</span>}
            </label>

            {field.description && (
              <p className="text-base text-gray-600 leading-relaxed">{field.description}</p>
            )}
          </div>

          {/* Form Input */}
          <div className="space-y-4">
            {isAddressField ? (
              <div className="space-y-3">
                {!showManualEntry ? (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={addressQuery}
                        onChange={(e) => {
                          setAddressQuery(e.target.value);
                          searchAddresses(e.target.value);
                        }}
                        placeholder="Enter postcode or start typing address..."
                        className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200 text-gray-900 text-base placeholder-gray-400"
                      />
                      {isSearching && (
                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                        </div>
                      )}
                    </div>

                    {addressError && addressResults.length === 0 && addressQuery.length >= 3 && !isSearching && (
                      <div className="mt-2 flex items-start space-x-2 text-sm text-gray-600 bg-gray-50 px-4 py-3 rounded-xl border border-gray-200">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{addressError}</span>
                      </div>
                    )}

                    {addressResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-2xl max-h-64 overflow-y-auto">
                        {addressResults.map((address, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleAddressSelect(address)}
                            className="w-full text-left px-4 py-4 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="flex items-start space-x-3">
                              <MapPin className="w-5 h-5 text-gray-900 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <div className="text-gray-900 font-medium">{address.formatted_address}</div>
                                {address.postcode && (
                                  <div className="text-sm text-gray-500 mt-0.5">{address.postcode}</div>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {addressQuery.length >= 3 && !isSearching && addressResults.length === 0 && !addressError && (
                      <div className="mt-2 text-sm text-gray-500 italic px-1">
                        💡 Tip: Try entering a UK postcode (e.g., SW1A 1AA) for best results
                      </div>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={value as string || ''}
                    onChange={handleChange}
                    placeholder="Enter full address manually"
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200 text-base"
                    required={isRequired}
                  />
                )}

                <button
                  type="button"
                  onClick={() => setShowManualEntry(!showManualEntry)}
                  className="text-sm text-gray-900 hover:text-black font-medium transition-colors"
                >
                  {showManualEntry ? '← Use address search' : 'Enter manually →'}
                </button>

                {field.examples && showManualEntry && (
                  <ExampleAnswers
                    examples={field.examples}
                    hint={field.exampleHint}
                    onSelect={(example) => setResponse(field.id, example)}
                    selectedValue={value}
                    fieldType={field.type}
                  />
                )}
              </div>
            ) : field.type === 'text' && (
              <div className="relative">
                <input
                  type={isPasswordField && !showPassword ? 'password' : 'text'}
                  value={value as string || ''}
                  onChange={handleChange}
                  placeholder={`Enter ${field.question.toLowerCase().replace('?', '').replace(/^what is (the )?/, '')}`}
                  className={clsx(
                    "w-full px-5 py-4 bg-gray-50 border-2 rounded-2xl transition-all duration-200 text-gray-900 text-base placeholder-gray-400",
                    validationError
                      ? "border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-200 focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                  )}
                  required={isRequired}
                  pattern={field.validation?.pattern}
                />

                {isPasswordField && (
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                )}

                {value && !validationError && !isPasswordField && (
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-gray-900" />
                  </div>
                )}
              </div>
            )}

            {field.type === 'text' && field.examples && !isAddressField && (
              <ExampleAnswers
                examples={field.examples}
                hint={field.exampleHint}
                onSelect={(example) => setResponse(field.id, example)}
                selectedValue={value}
                fieldType={field.type}
              />
            )}

            {field.type === 'textarea' && (
              <div className="relative">
                <textarea
                  value={value as string || ''}
                  onChange={handleChange}
                  placeholder={field.placeholder || `Enter ${field.question.toLowerCase().replace('?', '').replace(/^(what is (the )?|describe |list |provide )/, '')}`}
                  rows={field.rows || 4}
                  className={clsx(
                    "w-full px-5 py-4 bg-gray-50 border-2 rounded-2xl transition-all duration-200 text-gray-900 text-base placeholder-gray-400 resize-y",
                    validationError
                      ? "border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-200 focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                  )}
                  required={isRequired}
                />
                {value && !validationError && (
                  <div className="absolute right-4 top-4 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-gray-900" />
                  </div>
                )}
              </div>
            )}

            {field.type === 'textarea' && field.examples && (
              <ExampleAnswers
                examples={field.examples}
                hint={field.exampleHint}
                onSelect={(example) => setResponse(field.id, example)}
                selectedValue={value}
                fieldType="text"
              />
            )}

            {field.type === 'number' && (
              <div className="relative">
                <input
                  type="number"
                  value={value as number || ''}
                  onChange={handleChange}
                  placeholder={`Enter ${field.question.toLowerCase().replace('?', '').replace(/^what is (the )?/, '')}`}
                  className={clsx(
                    "w-full px-5 py-4 bg-gray-50 border-2 rounded-2xl transition-all duration-200 text-gray-900 text-base placeholder-gray-400",
                    validationError
                      ? "border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-200 focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                  )}
                  required={isRequired}
                  min={field.validation?.min}
                  max={field.validation?.max}
                />
                {value && !validationError && (
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-gray-900" />
                  </div>
                )}
              </div>
            )}

            {field.type === 'number' && field.examples && (
              <ExampleAnswers
                examples={field.examples}
                hint={field.exampleHint}
                onSelect={(example) => setResponse(field.id, Number(example.replace(/[^0-9.-]/g, '')))}
                selectedValue={value}
                fieldType={field.type}
              />
            )}

            {field.type === 'number' && field.smartDefaults && (() => {
              const smartDefault = getSmartDefault(field.id, responses);
              const smartHint = getSmartDefaultHint(field.id, responses);
              if (smartDefault !== undefined && smartHint) {
                return (
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setResponse(field.id, smartDefault)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-xl hover:from-black hover:to-gray-900 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <Sparkles className="w-4 h-4" />
                      <span className="text-sm font-medium">Auto-calculate</span>
                    </button>
                    <p className="text-sm text-gray-600">{smartHint}</p>
                  </div>
                );
              }
              return null;
            })()}

            {field.type === 'date' && (
              <div className="relative">
                <input
                  type="date"
                  value={value as string || ''}
                  onChange={handleChange}
                  className={clsx(
                    "w-full px-5 py-4 bg-gray-50 border-2 rounded-2xl transition-all duration-200 text-gray-900 text-base",
                    validationError
                      ? "border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-200 focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                  )}
                  required={isRequired}
                />
                {value && !validationError && (
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-gray-900" />
                  </div>
                )}
              </div>
            )}

            {field.type === 'date' && field.examples && (
              <ExampleAnswers
                examples={field.examples}
                hint={field.exampleHint}
                onSelect={(example) => setResponse(field.id, example)}
                selectedValue={value}
                fieldType={field.type}
              />
            )}

            {field.type === 'select' && field.options && (
              <div className="relative">
                <select
                  value={value as string || ''}
                  onChange={handleChange}
                  className={clsx(
                    "w-full px-5 py-4 bg-gray-50 border-2 rounded-2xl transition-all duration-200 text-gray-900 text-base appearance-none cursor-pointer",
                    validationError
                      ? "border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-200 focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                  )}
                  required={isRequired}
                >
                  <option value="">Select an option</option>
                  {field.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  {value && !validationError ? (
                    <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-gray-900" />
                    </div>
                  ) : (
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
              </div>
            )}

            {field.type === 'radio' && field.options && (
              <div className="space-y-3">
                {field.options.map((option) => (
                  <label
                    key={option}
                    className={clsx(
                      "flex items-center space-x-4 px-5 py-4 border-2 rounded-2xl cursor-pointer transition-all duration-200",
                      value === option
                        ? "border-gray-900 bg-gray-50 shadow-sm"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    )}
                  >
                    <div className="flex-shrink-0">
                      <input
                        type="radio"
                        value={option}
                        checked={value === option}
                        onChange={handleChange}
                        className="w-5 h-5 text-gray-900 border-gray-300 focus:ring-gray-900"
                        required={isRequired}
                      />
                    </div>
                    <span className="text-gray-900 font-medium text-base flex-1">{option}</span>
                    {value === option && (
                      <Check className="w-5 h-5 text-gray-900 flex-shrink-0" />
                    )}
                  </label>
                ))}
              </div>
            )}

            {field.type === 'checkbox' && field.options && (
              <div className="space-y-3">
                {field.options.map((option) => (
                  <label
                    key={option}
                    className={clsx(
                      "flex items-center space-x-4 px-5 py-4 border-2 rounded-2xl cursor-pointer transition-all duration-200",
                      (value as string[] || []).includes(option)
                        ? "border-gray-900 bg-gray-50 shadow-sm"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    )}
                  >
                    <div className="flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={(value as string[] || []).includes(option)}
                        onChange={() => handleCheckboxChange(option)}
                        className="w-5 h-5 text-gray-900 border-gray-300 rounded-lg focus:ring-gray-900"
                      />
                    </div>
                    <span className="text-gray-900 font-medium text-base flex-1">{option}</span>
                    {(value as string[] || []).includes(option) && (
                      <Check className="w-5 h-5 text-gray-900 flex-shrink-0" />
                    )}
                  </label>
                ))}
              </div>
            )}

            {field.type === 'file' && (
              <FileUploadField
                fieldId={field.id}
                value={value as string | undefined}
                acceptedFileTypes={field.acceptedFileTypes}
                maxFileSizeMB={field.maxFileSizeMB}
                placeholder={field.placeholder}
                isRequired={isRequired}
                onValueChange={(text) => setResponse(field.id, text)}
              />
            )}

            {validationError && (
              <div className="flex items-center space-x-3 text-red-600 bg-red-50 px-4 py-3 rounded-xl border border-red-100">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">{validationError}</span>
              </div>
            )}

            {value && !validationError && isRequired && (
              <div className="flex items-center space-x-3 text-gray-900 bg-gray-50 px-4 py-3 rounded-xl border border-gray-200">
                <Check className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">Perfect! Ready to continue.</span>
              </div>
            )}
          </div>

          {!isRequired && onSkip && (
            <div className="pt-6 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 italic flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  Optional field
                </p>
                <button
                  type="button"
                  onClick={onSkip}
                  className="inline-flex items-center space-x-2 px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200"
                >
                  <span>Skip</span>
                  <SkipForward className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
